import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight } from 'react-icons/fi';
import PropertyCard from '../PropertyCard';
import { Eyebrow, SectionHeader, SectionSubtitle, SectionTitle } from '../ui';
import { propertyService } from '../../services/api';
import { locationService } from '../../services/locations';
import { LocationSummary } from '../../types/location';
import { Property } from '../../types';
import { similarSectionTitle } from './similarProperties';

const SIMILAR_LIMIT = 6;

const Section = styled.section`
  padding: 2.5rem 0 4rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  scroll-margin-top: 5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  margin-top: 1.75rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 0.95rem;

  svg { transition: transform 0.2s ease; }
  &:hover svg { transform: translateX(3px); }
`;

interface SimilarPropertiesProps {
  property: Pick<Property, 'id' | 'condominium_name'> & { address?: unknown };
  unavailable?: boolean;
}

/** Some quando não há semelhantes ou a API falha: nunca derruba a página. */
const SimilarProperties: React.FC<SimilarPropertiesProps> = ({ property, unavailable = false }) => {
  const [items, setItems] = useState<Property[]>([]);
  const [place, setPlace] = useState<LocationSummary | null>(null);

  useEffect(() => {
    let active = true;
    setItems([]);
    propertyService
      .getSimilar(String(property.id), SIMILAR_LIMIT)
      .then((data) => {
        if (active) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setItems([]);
      });
    setPlace(null);
    locationService
      .getForProperty(String(property.id))
      .then((data) => {
        if (active) setPlace(data?.path ? data : null);
      })
      .catch(() => {
        if (active) setPlace(null);
      });
    return () => {
      active = false;
    };
  }, [property.id]);

  if (items.length === 0) return null;

  return (
    <Section id="imoveis-semelhantes" aria-labelledby="imoveis-semelhantes-titulo">
      <SectionHeader>
        <Eyebrow>{unavailable ? 'Opções disponíveis' : 'Continue explorando'}</Eyebrow>
        <SectionTitle id="imoveis-semelhantes-titulo">
          {similarSectionTitle(property, items)}
        </SectionTitle>
        <SectionSubtitle>
          {unavailable
            ? 'Este já saiu, mas estes estão disponíveis e têm o mesmo perfil.'
            : 'Outros imóveis disponíveis com perfil parecido com este.'}
        </SectionSubtitle>
      </SectionHeader>
      <Grid>
        {items.map((item) => (
          <PropertyCard key={item.id} property={item} />
        ))}
      </Grid>
      <ViewAllLink to={place ? place.path : '/imoveis'}>
        {place ? `Ver todos os imóveis em ${place.name}` : 'Ver todos os imóveis'}{' '}
        <FiArrowRight size={16} />
      </ViewAllLink>
    </Section>
  );
};

export default SimilarProperties;
